import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { FiUserCheck, FiSearch, FiRefreshCcw, FiPlus, FiDownload, FiCheck, FiX, FiTrash2 } from 'react-icons/fi'
import { fetchEnrollments } from '../../store/slices/adminSlice'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import Modal from '../../components/Modal/Modal'
import FormInput from '../../components/FormInput/FormInput'
import './Enrollments.css'

const Enrollments = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { enrollments, isLoading, error } = useSelector((state) => state.admin)
  
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)
  const [selectedEnrollments, setSelectedEnrollments] = useState([])
  const [showBulkModal, setShowBulkModal] = useState(false)
  const [bulkAction, setBulkAction] = useState('')
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)

  useEffect(() => {
    loadEnrollments()
  }, [page, statusFilter])

  const loadEnrollments = async () => {
    const params = {
      page,
      limit: 25,
    }
    if (statusFilter !== 'all') {
      params.status = statusFilter
    }
    dispatch(fetchEnrollments(params))
  }

  const handleStatusChange = async (enrollmentId, newStatus) => {
    try {
      await adminApi.patch(`/enrollments/${enrollmentId}`, { status: newStatus })
      loadEnrollments()
    } catch (err) {
      console.error('Status update failed:', err)
    }
  }

  const handleBulkAction = async () => {
    if (selectedEnrollments.length === 0 || !bulkAction) return
    
    setBulkLoading(true)
    try {
      const promises = selectedEnrollments.map((id) => {
        if (bulkAction === 'delete') {
          return adminApi.delete(`/enrollments/${id}`)
        } else {
          return adminApi.patch(`/enrollments/${id}`, { status: bulkAction })
        }
      })
      await Promise.all(promises)
      setSelectedEnrollments([])
      setShowBulkModal(false)
      setBulkAction('')
      loadEnrollments()
    } catch (err) {
      console.error('Bulk action failed:', err)
    } finally {
      setBulkLoading(false)
    }
  }

  const handleExport = async () => {
    try {
      const enrollmentsList = enrollments?.enrollments || enrollments?.data || []
      const csvData = [
        ['Student Name', 'Email', 'Batch', 'Course', 'Status', 'Joined Date'].join(','),
        ...enrollmentsList.map((e) =>
          [
            `"${(e.user?.firstName || '')} ${(e.user?.lastName || '')}"`,
            `"${e.user?.email || ''}"`,
            `"${e.batch?.batchName || ''}"`,
            `"${e.batch?.course?.title || e.course?.title || ''}"`,
            `"${e.status || ''}"`,
            `"${e.joinedAt ? new Date(e.joinedAt).toLocaleDateString() : ''}"`,
          ].join(',')
        ),
      ].join('\n')

      const blob = new Blob([csvData], { type: 'text/csv' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `enrollments-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(url)
      setShowExportModal(false)
    } catch (err) {
      console.error('Export failed:', err)
    }
  }

  const toggleEnrollmentSelection = (enrollmentId) => {
    setSelectedEnrollments((prev) =>
      prev.includes(enrollmentId)
        ? prev.filter((id) => id !== enrollmentId)
        : [...prev, enrollmentId]
    )
  }

  const toggleSelectAll = () => {
    const enrollmentsList = enrollments?.enrollments || enrollments?.data || []
    if (selectedEnrollments.length === enrollmentsList.length) {
      setSelectedEnrollments([])
    } else {
      setSelectedEnrollments(enrollmentsList.map((e) => e._id))
    }
  }

  const filteredEnrollments = useMemo(() => {
    const enrollmentsList = enrollments?.enrollments || enrollments?.data || []
    if (!Array.isArray(enrollmentsList)) return []
    const query = searchQuery.toLowerCase()
    return enrollmentsList.filter((enrollment) => {
      const matchesSearch =
        !query ||
        [
          enrollment.user?.firstName,
          enrollment.user?.lastName,
          enrollment.user?.email,
          enrollment.batch?.batchName,
        ]
          .filter(Boolean)
          .some((field) => field.toLowerCase().includes(query))
      return matchesSearch
    })
  }, [enrollments, searchQuery])

  const columns = [
    {
      key: 'select',
      label: (
        <input
          type="checkbox"
          checked={
            filteredEnrollments.length > 0 &&
            selectedEnrollments.length === filteredEnrollments.length
          }
          onChange={toggleSelectAll}
        />
      ),
      render: (row) => (
        <input
          type="checkbox"
          checked={selectedEnrollments.includes(row._id)}
          onChange={() => toggleEnrollmentSelection(row._id)}
        />
      ),
    },
    {
      key: 'student',
      label: 'Student',
      render: (row) => (
        <div>
          <strong>
            {row.user?.firstName} {row.user?.lastName}
          </strong>
          <span className="enrollment-email">{row.user?.email}</span>
        </div>
      ),
    },
    {
      key: 'batch',
      label: 'Batch',
      render: (row) => row.batch?.batchName || '—',
    },
    {
      key: 'course',
      label: 'Course',
      render: (row) => row.batch?.course?.title || row.course?.title || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => (
        <div className="status-actions">
          <StatusBadge status={row.status} />
          <div className="status-buttons">
            {row.status !== 'active' && (
              <Button
                size="small"
                variant="secondary"
                onClick={() => handleStatusChange(row._id, 'active')}
                title="Approve/Activate"
              >
                <FiCheck size={14} />
              </Button>
            )}
            {row.status !== 'dropped' && (
              <Button
                size="small"
                variant="danger"
                onClick={() => handleStatusChange(row._id, 'dropped')}
                title="Reject/Drop"
              >
                <FiX size={14} />
              </Button>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'joinedAt',
      label: 'Joined',
      render: (row) => {
        if (!row.joinedAt) return '—'
        return new Date(row.joinedAt).toLocaleDateString()
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/enrollments/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="enrollments-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Enrollment Management</p>
          <h1>All Enrollments</h1>
          <p className="subhead">Manage student enrollments and status</p>
        </div>
        <div className="header-actions">
          {selectedEnrollments.length > 0 && (
            <Button
              variant="secondary"
              onClick={() => setShowBulkModal(true)}
            >
              Bulk Actions ({selectedEnrollments.length})
            </Button>
          )}
          <Button
            variant="secondary"
            onClick={() => setShowExportModal(true)}
          >
            <FiDownload size={18} />
            Export
          </Button>
          <Button
            variant="secondary"
            onClick={loadEnrollments}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
          <Button onClick={() => navigate('/enrollments/new')}>
            <FiPlus size={18} />
            Create Enrollment
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search by student name or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'active', label: 'Active' },
            { value: 'completed', label: 'Completed' },
            { value: 'dropped', label: 'Dropped' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredEnrollments}
        loading={isLoading}
        emptyMessage="No enrollments found"
      />

      {/* Bulk Actions Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => {
          setShowBulkModal(false)
          setBulkAction('')
        }}
        title="Bulk Actions"
        size="medium"
      >
        <div className="bulk-actions-form">
          <p>
            Apply action to <strong>{selectedEnrollments.length}</strong> selected enrollment(s)
          </p>
          <Select
            label="Action"
            name="bulkAction"
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            options={[
              { value: '', label: 'Select an action' },
              { value: 'active', label: 'Approve/Activate' },
              { value: 'completed', label: 'Mark as Completed' },
              { value: 'dropped', label: 'Reject/Drop' },
              { value: 'delete', label: 'Delete' },
            ]}
            required
          />
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => {
                setShowBulkModal(false)
                setBulkAction('')
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              loading={bulkLoading}
              disabled={!bulkAction}
              variant={bulkAction === 'delete' ? 'danger' : 'primary'}
            >
              Apply Action
            </Button>
          </div>
        </div>
      </Modal>

      {/* Export Modal */}
      <Modal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        title="Export Enrollments"
        size="small"
      >
        <div className="export-options">
          <p>Export all enrollments to CSV file?</p>
          <div className="modal-actions">
            <Button
              variant="secondary"
              onClick={() => setShowExportModal(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleExport}>
              <FiDownload size={18} />
              Export CSV
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default Enrollments

